import React from 'react';
import PageHeader from '../components/PageHeader';
import { usePageMeta } from '../hooks/usePageMeta';

const BODY: React.CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: '0.92rem',
  lineHeight: 1.65,
  margin: '0 0 10px',
};

const LIST: React.CSSProperties = {
  ...BODY,
  paddingLeft: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 'var(--space-xl)' }}>
      <h2 className="about-section__heading" style={{ marginBottom: 'var(--space-sm)' }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function PrivacyPage() {
  usePageMeta({ title: 'Privacy', description: 'How Food Safety Digest handles personal data.' });
  return (
    <div className="container" style={{ maxWidth: 760 }}>
      <PageHeader
        title="Privacy Policy"
        tagline="How Food Safety Digest handles your data."
      />

      <Section title="Who we are">
        <p style={BODY}>
          Food Safety Digest (fsnu.info) is a monitoring service for food safety
          professionals. It aggregates public regulatory data — RASFF notifications,
          legislation, recalls and scientific updates — into dashboards and email digests.
        </p>
      </Section>

      <Section title="What we collect">
        <ul style={LIST}>
          <li>
            <strong>Newsletter subscriptions</strong> — the email address you subscribe
            with, an optional name, and your notification preferences (digest frequency,
            product types and categories).
          </li>
          <li>
            <strong>Feedback submissions</strong> — the message you send through our
            feedback form, plus your name and email address if you choose to provide them.
          </li>
          <li>
            <strong>Aggregate usage analytics</strong> — collected with a self-hosted
            Plausible instance. This is cookieless: no cookies are set, no personal
            profiles are built, and there is no cross-site tracking.
          </li>
          <li>
            <strong>Public LinkedIn comments</strong> — comments posted publicly on our
            LinkedIn page posts are read so we can respond and improve the service. We
            never store commenters' names. Comment text is kept for at most 48 hours and
            is then automatically deleted, leaving only anonymous counts (how many
            comments a post received, by topic). This follows LinkedIn's data storage
            rules for its Community Management API.
          </li>
        </ul>
      </Section>

      <Section title="Why we collect it">
        <p style={BODY}>
          We process this data on the basis of legitimate interest: running the service
          you signed up for and improving it based on what users tell us. We do not use
          your data for advertising or profiling.
        </p>
      </Section>

      <Section title="How long we keep it">
        <ul style={LIST}>
          <li>Newsletter subscriptions: until you unsubscribe.</li>
          <li>Feedback you submit here: until it has been actioned and is no longer needed.</li>
          <li>
            LinkedIn comment text: at most 48 hours, then automatically deleted. Commenters'
            names are never stored at all. Anonymous engagement counts for our own posts are
            kept for up to a year.
          </li>
          <li>Analytics: aggregate only, with no individual profile to delete.</li>
        </ul>
      </Section>

      <Section title="Your rights">
        <p style={BODY}>
          You can request access to, or deletion of, any data we hold about you at any
          time by emailing{' '}
          <a href="mailto:info@fsnu.info" className="admin-link">info@fsnu.info</a>.
          Every newsletter also contains a one-click unsubscribe link.
        </p>
      </Section>

      <Section title="No selling — and the processors we do use">
        <p style={BODY}>
          We never sell or rent your personal data, and we do not share it for advertising
          or profiling. We do rely on a small number of processors to run the service:
        </p>
        <ul style={LIST}>
          <li>
            <strong>Resend</strong> — delivers the newsletter and our alert emails, and
            receives mail sent to info@fsnu.info.
          </li>
          <li>
            <strong>Anthropic (Claude)</strong> — summarises feedback submitted through
            the form on this site once a month so we can turn it into product improvements.
            LinkedIn comment text is never sent to it.
          </li>
          <li>
            <strong>LinkedIn</strong> — when we publish to our company page and read the
            public engagement on our own posts.
          </li>
        </ul>
      </Section>

      <Section title="Where your data lives">
        <p style={BODY}>
          Our database, application servers and analytics run on our own infrastructure in
          the European Union. Two exceptions are worth naming: our email provider processes
          mail in the EU but is a US company, and the monthly feedback summary described
          above is generated by Anthropic's Claude, which may process that text outside the EU.
        </p>
      </Section>
    </div>
  );
}
